import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ListVideo, Ban, Play, Trash2, MoreHorizontal } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import {
  Button, IconButton, Skeleton, Tabs, TabsList, TabsTrigger, TabsContent,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import { streamStatusTone } from '../live-stream-status';
import { useLiveStream } from '@/api/media/live-live-streams';
import { JobOverviewPanel } from './JobOverviewPanel';
import { JobMonitorPanel } from './JobMonitorPanel';
import { RecordingTab } from './RecordingTab';
import { ConnectionTab } from './ConnectionTab';
import { useJobActions, confirmProps } from './use-job-actions';
import styles from './CurrentJobDetail.module.css';

interface Props {
  serviceId: string;
}

const VALID_TABS = ['overview', 'monitor', 'recording', 'connection'] as const;

export function LiveCurrentJobDetail({ serviceId }: Props) {
  const navigate = useNavigate();
  const { streamId = '' } = useParams<{ streamId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const base = `/services/live/${serviceId}`;
  const jobsPath = `${base}/current-jobs`;

  const { data: stream, isPending, isError } = useLiveStream(serviceId, streamId);

  const { pendingAction, setPendingAction, handleConfirm, isBusy } = useJobActions(
    serviceId,
    { onDeleteSuccess: () => navigate(jobsPath) },
  );

  const requestedTab = searchParams.get('tab') ?? 'overview';
  const tab = (VALID_TABS as readonly string[]).includes(requestedTab) ? requestedTab : 'overview';

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'overview') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  if (isPending) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 28, width: 260, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 120 }} />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl">
        <Alert tone="warning" title="Failed to load stream">
          Could not retrieve stream details. Check your connection and try again.
        </Alert>
      </Container>
    );
  }

  if (!stream) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<ListVideo size={20} />}
          title="Stream not found"
          description="This live stream doesn't exist or is no longer available."
          actions={<Button variant="secondary" onClick={() => navigate(jobsPath)}>Back to Current Jobs</Button>}
        />
      </Container>
    );
  }

  const showRecording = stream.recording_enabled === true;
  const isForbidden = stream.status?.toLowerCase() === 'forbidden';

  const conf = pendingAction ? confirmProps(pendingAction) : null;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <LiveConsoleBreadcrumbs
            trail={[
              { label: 'Current Jobs', slug: 'current-jobs' },
              { label: stream.stream_name || streamId },
            ]}
          />
        }
        icon={<ListVideo size={22} />}
        title={stream.stream_name || streamId}
        description={[stream.app_name, stream.stream_name].filter(Boolean).join(' · ') || undefined}
        actions={
          <div className={styles.headerActions}>
            <StatusPill tone={streamStatusTone(stream.status)}>{stream.status || 'Unknown'}</StatusPill>
            {isForbidden ? (
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Play size={14} />}
                disabled={isBusy}
                onClick={() => setPendingAction({ kind: 'resume', row: stream })}
              >
                Resume
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Ban size={14} />}
                disabled={isBusy}
                onClick={() => setPendingAction({ kind: 'forbid', row: stream })}
              >
                Forbid
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" aria-label="More job actions">
                  <MoreHorizontal size={18} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  tone="danger"
                  onSelect={() => setPendingAction({ kind: 'delete', row: stream })}
                >
                  <Trash2 size={14} /> Delete job
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="monitor">Live Monitor</TabsTrigger>
          {showRecording && <TabsTrigger value="recording">Recording</TabsTrigger>}
          <TabsTrigger value="connection">Connection</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className={styles.tabContent}>
            <JobOverviewPanel stream={stream} />
          </div>
        </TabsContent>

        <TabsContent value="monitor">
          <div className={styles.tabContent}>
            <JobMonitorPanel serviceId={serviceId} streamId={streamId} />
          </div>
        </TabsContent>

        {showRecording && (
          <TabsContent value="recording">
            <div className={styles.tabContent}>
              <RecordingTab serviceId={serviceId} stream={stream} />
            </div>
          </TabsContent>
        )}

        <TabsContent value="connection">
          <div className={styles.tabContent}>
            <ConnectionTab serviceId={serviceId} stream={stream} />
          </div>
        </TabsContent>
      </Tabs>

      {pendingAction && conf && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setPendingAction(null)}
          title={conf.title}
          description={conf.description}
          confirmLabel={conf.confirmLabel}
          destructive={conf.destructive}
          requireText={conf.requireText}
          loading={isBusy}
          onConfirm={handleConfirm}
        />
      )}
    </Container>
  );
}

export default LiveCurrentJobDetail;
